/**
 * Decide the single most valuable next action for a lead/deal, so the detail
 * pages can surface one clear "do this next" hero instead of leaving the user
 * to infer it from a wall of cards.
 *
 * Returns { key, label, hint, tone } or null. `key` is a stable action id the
 * page maps to a handler (reply / assign / quote / book / follow_up / deliver).
 */
export function nextBestAction({ stage, unread, hasConsultant, quotes = [], sale = null, hasOpenAppt = false } = {}) {
  const closed = stage === 'DELIVERED' || stage === 'LOST';

  if (unread) {
    return { key: 'reply', label: 'Reply now', hint: 'The customer is waiting on a response.', tone: 'danger' };
  }
  if (!hasConsultant && !closed) {
    return { key: 'assign', label: 'Assign a consultant', hint: 'No one owns this lead yet.', tone: 'warning' };
  }
  if (sale && sale.status === 'PENDING_DELIVERY') {
    return { key: 'deliver', label: 'Arrange delivery', hint: `Sale ${sale.saleNumber} is awaiting delivery.`, tone: 'success' };
  }
  const hasSentQuote = quotes.some((q) => q.status === 'SENT');
  if (hasSentQuote) {
    return { key: 'follow_up', label: 'Follow up on the quote', hint: 'A quote is out — chase the decision.', tone: 'accent' };
  }
  if (stage === 'NEGOTIATION' && quotes.length === 0) {
    return { key: 'quote', label: 'Create a quote', hint: 'This deal is in Quoting with no quote yet.', tone: 'accent' };
  }
  if (stage === 'TEST_DRIVE' && !hasOpenAppt) {
    return { key: 'book', label: 'Book the test drive', hint: 'Get them behind the wheel to move the sale forward.', tone: 'accent' };
  }
  if ((stage === 'CONFIRMED' || stage === 'DELIVERY_BOOKED' || stage === 'RETAILED') && !hasOpenAppt) {
    return { key: 'book', label: 'Book an appointment', hint: 'Get them in to move the deal forward.', tone: 'accent' };
  }
  if (stage === 'INBOUND' || stage === 'CONTACTED') {
    return { key: 'reply', label: 'Continue the conversation', hint: 'Qualify the lead and build rapport.', tone: 'accent' };
  }
  return null;
}

/** Map a next-action tone to a CSS colour var. */
export function actionColor(tone) {
  return tone === 'danger' ? 'var(--danger)'
    : tone === 'warning' ? 'var(--warning)'
    : tone === 'success' ? 'var(--success)'
    : 'var(--accent-primary)';
}
